import React, { useEffect, useState } from "react";
import { NavLink, useParams } from "react-router-dom";
import { Message, toaster } from "rsuite";
import { useTranslation } from "react-i18next";
import parse, { Element as HtmlParserElement } from "html-react-parser";
import { template } from "lodash";
import cn from "classnames";

import constants from "utils/constants";

import { isDooapGuestTenantAllowed } from "api/environments/tenants/guest-tenants-api";
import { getErrorMessage } from "api/defaults";

import {
    EnrollCache,
    EnrollStep,
    StepStatus,
} from "store/environments/tenants/enroll-tenant/enroll-tenant-types";

import css from "./GuestTenantsStep.module.css";

interface Props {
    activeStep: EnrollStep | null;
    orchestratorInstanceId: string;
    activeEnvMoniker: string;

    fetchEnrollCache: (
        environmentMoniker: string,
        orchestratorInstanceId: string
    ) => Promise<[EnrollCache | null, number]>;
    setActiveStepData: (data: string | null) => void;
    showLoader: (isVisible: boolean) => void;
}

const GuestTenantsStep: React.FC<Props> = ({
    activeStep,
    orchestratorInstanceId,
    activeEnvMoniker,
    fetchEnrollCache,
    setActiveStepData,
    showLoader,
}: Props) => {
    const { t } = useTranslation();

    const { environmentId } = useParams<{ environmentId: string }>();

    const guestTenantsPageLinkClassName = "guest-tenants-page-link";

    const [isFetched, setIsFetched] = useState<boolean>(false);

    const [addDooapGuestTenant, setAddDooapGuestTenant] =
        useState<boolean>(false);

    const [isGuestTenantAllowed, setIsGuestTenantAllowed] =
        useState<boolean>(true);

    const parser = (input: string) =>
        parse(input, {
            replace: (domNode) => {
                if (
                    domNode instanceof HtmlParserElement &&
                    domNode.attribs.class === guestTenantsPageLinkClassName
                ) {
                    return (
                        <NavLink
                            to={`/environments/${environmentId}/enroll-tenant/${orchestratorInstanceId}/guest-tenants`}
                        >
                            {t(
                                "enrollTenant.guestTenants.guestTenantsLinkText"
                            )}
                        </NavLink>
                    );
                }
            },
        });

    const fetchData = (environmentMoniker: string, enrollId: string) => {
        if (
            environmentMoniker &&
            enrollId &&
            activeStep?.status !== StepStatus.Success
        ) {
            showLoader(true);
            fetchEnrollCache(environmentMoniker, enrollId)
                .then(([payload, statusCode]) => {
                    if (payload && statusCode === constants.statusCode.OK) {
                        if (payload.tenantMoniker) {
                            return isDooapGuestTenantAllowed(
                                activeEnvMoniker,
                                payload.tenantMoniker
                            ).then(([payload, statusCode]) => {
                                if (statusCode === constants.statusCode.OK) {
                                    setIsGuestTenantAllowed(payload ?? false);
                                    setAddDooapGuestTenant(payload ?? false);
                                } else {
                                    const errorMessage = getErrorMessage(
                                        String(payload),
                                        statusCode
                                    );
                                    toaster.push(
                                        <Message type="error" showIcon>
                                            {errorMessage}
                                        </Message>,
                                        {
                                            duration:
                                                constants.errorAlertDurationMsec,
                                        }
                                    );
                                }
                            });
                        }
                    }

                    return Promise.resolve();
                })
                .then(() => {
                    setIsFetched(true);
                })
                .finally(() => {
                    showLoader(false);
                });
        }
    };

    useEffect(() => {
        if (activeEnvMoniker && orchestratorInstanceId && !isFetched) {
            fetchData(activeEnvMoniker, orchestratorInstanceId);
        }
    }, [activeEnvMoniker, orchestratorInstanceId, isFetched]);

    useEffect(() => {
        setActiveStepData(
            JSON.stringify({
                addDooapGuestTenant: addDooapGuestTenant,
            })
        );
    }, [addDooapGuestTenant]);

    const content = (
        <div className={cn(css.guestTenantLink, css.stepContent)}>
            {parser(
                template(t("enrollTenant.guestTenants.message"))({
                    guestTenantsPageLinkClassName:
                        guestTenantsPageLinkClassName,
                })
            )}
            {!isGuestTenantAllowed && (
                <p className={css.warningMsg}>
                    {t("enrollTenant.guestTenants.warningMsg")}
                </p>
            )}
        </div>
    );

    return activeStep && content;
};

export default GuestTenantsStep;
